import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useEffect, useState, type ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { NameField, type FieldServerError } from './NameField';
import { SwitchRow } from './SwitchRow';
import { createCatalogMake } from '../../../api/admin-vehicle-catalog';
import { catalogName } from '../_lib/catalogForms';
import { useCatalogWrite } from '../_lib/useCatalogWrite';

/**
 * «Добавить марку» (AUT-437), after `vehicle-catalog.html?state=add|conflict`.
 * `POST /admin/vehicle-catalog/makes`; on success the caller opens the new
 * make's drawer at once (a make without models is not in the app). A 409
 * names the entry that already has the name, with a link to open it.
 */
const schema = z.object({ name: catalogName, popular: z.boolean(), onSale: z.boolean() });
type Values = z.infer<typeof schema>;
const DEFAULTS: Values = { name: '', popular: false, onSale: true };

export interface CreateMakeDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (makeId: string) => void;
  onOpenMake: (makeId: string) => void;
}

export const CreateMakeDialog = ({
  open,
  onClose,
  onCreated,
  onOpenMake,
}: CreateMakeDialogProps): ReactElement => {
  const { t } = useTranslation();
  const write = useCatalogWrite();
  const [serverError, setServerError] = useState<FieldServerError | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (open) {
      form.reset(DEFAULTS);
      setServerError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (v: Values): Promise<void> => {
    const outcome = await write.run(
      () => createCatalogMake({ name: v.name, popular: v.popular, onSale: v.onSale }),
      { successKey: 'vehicleCatalog.createDialog.success' },
    );
    if (outcome.ok) onCreated(outcome.result.id);
    else if (outcome.error.kind === 'conflict' || outcome.error.kind === 'tooLong') {
      setServerError(outcome.error);
    }
  };

  return (
    <Dialog open={open} onClose={write.busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('vehicleCatalog.createDialog.title')}
        <Typography variant="body2" color="text.secondary">
          {t('vehicleCatalog.createDialog.subtitle')}
        </Typography>
      </DialogTitle>
      <form noValidate onSubmit={form.handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <NameField
                {...field}
                onChange={(value) => {
                  setServerError(null);
                  field.onChange(value);
                }}
                label={t('vehicleCatalog.name.label')}
                errorKey={fieldState.error?.message}
                serverError={serverError}
                onOpenMake={onOpenMake}
                disabled={write.busy}
                focusOnMount
              />
            )}
          />
          <FlagSwitches control={form.control} disabled={write.busy} />
          <Alert severity="info">{t('vehicleCatalog.createDialog.hint')}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={write.busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={write.busy} loading={write.busy}>
            {t('vehicleCatalog.createDialog.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const FlagSwitches = ({
  control,
  disabled,
}: {
  control: ReturnType<typeof useForm<Values>>['control'];
  disabled: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      {(['popular', 'onSale'] as const).map((flag) => (
        <Controller
          key={flag}
          control={control}
          name={flag}
          render={({ field }) => (
            <SwitchRow
              label={t(`vehicleCatalog.toggles.${flag}`)}
              hint={t(`vehicleCatalog.toggles.${flag}Hint`)}
              checked={field.value}
              disabled={disabled}
              onChange={field.onChange}
            />
          )}
        />
      ))}
    </>
  );
};
